import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import { useItineraryContext } from './ChatTripPlannerPage/contexts/ItineraryContext'
import { useTripListPricingContext } from './ChatTripPlannerPage/contexts/TripListPricingContext'
import { useCartButtonSummary } from './ChatTripPlannerPage/hooks/useCartButtonSummary'
import { useFetchCartPanelOffers } from './ChatTripPlannerPage/hooks/useFetchCartPanelOffers'
import { useTripPlannerEventBus } from './ChatTripPlannerPage/contexts/TripPlannerEventBus'
import CartButton from './ChatTripPlannerPage/components/CartButton'
import DownloadItineraryButton from './ChatTripPlannerPage/components/DownloadItineraryButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineCommentEditIcon from 'LuxKit/Icons/line/LineCommentEditIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import Group from 'components/utils/Group'
import ContactOptions from 'components/Common/ContactOptions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import {
  TripPlannerStickyHeader,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import { useDeleteTrip, useRecentTrips, useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { useRenameChatMutation } from 'tripPlanner/hooks/api/chat'
import { useChatHistory } from './ChatTripPlannerPage/hooks/useChatHistory'
import { formatChatLabel } from './ChatTripPlannerPage/utils/format'
import { findClosestChat } from './ChatTripPlannerPage/utils/findClosestChat'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import TripPlannerDrawer from './TripPlannerDrawer'
import Clickable from 'components/Common/Clickable'
import NavBarRegionButton from 'components/NavKit/NavBarButtons/NavBarRegionButton'

const TertiaryHeader = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(4)} ${rem(16)};
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const StyledStickyHeader = styled(TripPlannerStickyHeader)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

interface Props {
  chatId: number | undefined
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  onDevModeToggle: () => void
  className?: string
}

function TripPlannerHeader({
  chatId,
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  onDevModeToggle,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)
  const eventBus = useTripPlannerEventBus()
  const { itineraryData, hasItineraryData } = useItineraryContext()
  const { luxPlusEnabled } = useTripListPricingContext()
  useFetchCartPanelOffers(itineraryData, true)
  const cartSummary = useCartButtonSummary(itineraryData, isLoggedIn, luxPlusEnabled)

  const handleCartClick = useCallback(() => {
    eventBus.dispatch({ type: 'open_cart_panel' })
  }, [eventBus])

  const {
    chatHistory, recentChats, refetchChatList, deleteChatMutation,
  } = useChatHistory({ isLoggedIn })

  const { data: previousTrips, refetch: refetchTrips } = useRecentTrips()
  const { mutateAsync: renameChatMutation } = useRenameChatMutation()
  const { mutateAsync: deleteTripMutation } = useDeleteTrip({
    onSuccess: () => {
      refetchTrips()
    },
    onError: (error) => {
      console.error('[TripChat] Failed to delete trip', error)
      showSnackbar(intl.formatMessage({
        id: 'FtZ79RamZe2rLll_1NZF',
        defaultMessage: 'Unable to delete trip. Please try again.',
        description: 'Error snackbar message when trip deletion fails in the trip planner AI chat',
      }), 'critical')
    },
  })
  const { mutateAsync: updateTripMutation } = useUpdateTrip({
    onError: (error) => {
      console.error('[TripChat] Failed to rename trip', error)
      showSnackbar(intl.formatMessage({
        id: '5NH9uh8HGpEvAfHkilEf',
        defaultMessage: 'Unable to rename trip. Please try again.',
        description: 'Error snackbar message when trip rename fails in the trip planner AI chat',
      }), 'critical')
    },
  })

  // ── Delete handlers ──

  const getChatDisplayName = useCallback((chat: TripPlannerChat | undefined) => {
    if (!chat) {
      return intl.formatMessage({
        id: '-LmD19gmaBgkOCUWrnKU',
        defaultMessage: 'this chat',
        description: 'Fallback display name for a trip planner chat when no chat is found',
      })
    }
    const trimmedName = chat.name?.trim()
    if (trimmedName) {
      return trimmedName
    }
    return formatChatLabel(getChatLastActivityTimestamp(chat), intl)
  }, [intl])

  const handleDeleteChat = useCallback(async(chatIdToDelete: number) => {
    const chatToDelete = chatHistory.find((chat) => chat.id === chatIdToDelete)
    const chatName = getChatDisplayName(chatToDelete)

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={chatName} />,
    )

    if (!confirmed) {
      return
    }

    const fallbackChatId = findClosestChat(chatHistory, chatIdToDelete)

    try {
      await deleteChatMutation({ chatId: chatIdToDelete })
      await refetchChatList()
      onChatDeleted(chatIdToDelete, fallbackChatId)
    } catch(error) {
      console.error('[TripChat] Failed to delete chat', error)
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [chatHistory, showModal, deleteChatMutation, refetchChatList, getChatDisplayName, onChatDeleted, intl])

  const handleDeleteTrip = useCallback(async(tripIdToDelete: string) => {
    const tripToDelete = previousTrips?.find((trip) => trip.id === tripIdToDelete)
    const tripName = tripToDelete?.name ?? intl.formatMessage({
      id: 'qNXsaB7GsvlaNr5QLIZf',
      defaultMessage: 'Untitled Trip',
      description: 'Fallback name for a trip without a title in the delete confirmation modal',
    })

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={tripName} type="trip" />,
    )

    if (!confirmed) {
      return
    }

    await deleteTripMutation(tripIdToDelete)
  }, [previousTrips, showModal, deleteTripMutation, intl])

  const handleRenameChat = useCallback(async(chatIdToRename: number, name: string) => {
    try {
      await renameChatMutation({ chatId: chatIdToRename, name })
      await refetchChatList()
    } catch(error) {
      console.error('[TripChat] Failed to rename chat', error)
      showSnackbar(intl.formatMessage({
        id: 'tvKzJmAgeic_SZz_ioDb',
        defaultMessage: 'Unable to rename chat. Please try again.',
        description: 'Error snackbar message when chat rename fails in the trip planner AI chat',
      }), 'critical')
      throw error
    }
  }, [intl, refetchChatList, renameChatMutation])

  const handleRenameTrip = useCallback(async(tripIdToRename: string, name: string) => {
    await updateTripMutation({ tripId: tripIdToRename, name })
    await refetchTrips()
  }, [refetchTrips, updateTripMutation])

  // ── Drawer ──

  const handleOpenDrawer = useCallback(() => {
    showModal(
      <TripPlannerDrawer
        onStartNewChat={onStartNewChat}
        recentChats={recentChats}
        recentTrips={previousTrips}
        onChatSelect={onChatSelect}
        onChatRename={handleRenameChat}
        onTripRename={handleRenameTrip}
        onChatDelete={handleDeleteChat}
        onTripDelete={handleDeleteTrip}
        isStaff={isStaff}
        isDevMode={isDevMode}
        onDevModeToggle={onDevModeToggle}
      />,
    )
  }, [showModal, onStartNewChat, recentChats, previousTrips, onChatSelect, handleRenameChat, handleRenameTrip, handleDeleteChat, handleDeleteTrip, isStaff, isDevMode, onDevModeToggle])

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const backButtonText = intl.formatMessage({
    id: 'uvkQRhGsEtSZkBcb2NHS',
    defaultMessage: 'Back to Luxury Escapes',
    description: 'Navigation button text to return to the main Luxury Escapes site from TripPlanner',
  })

  const giveFeedbackText = intl.formatMessage({
    id: 'DwyX2RGwDgu5GbANxFYx',
    defaultMessage: 'Give feedback',
    description: 'Button text in the TripPlanner header to open the feedback survey for the feature',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const betaLabel = intl.formatMessage({
    id: 'gO283xrN3xwQSsK25Jy9',
    defaultMessage: 'Beta',
    description: 'Beta badge label for AI TripPlanner in the header in the trip planner AI chat',
  })

  const showCartButton = !!itineraryData

  const rightContent = useMemo(() => {
    if (isLoggedIn) {
      return (
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {hasItineraryData && <DownloadItineraryButton chatId={chatId} />}
          <NavBarRegionButton />
          {showCartButton && <CartButton
            itemCount={cartSummary.bookableItemCount}
            totalPrice={cartSummary.estimatedTotal}
            isLoading={cartSummary.isLoading}
            onClick={handleCartClick}
          />}
        </Group>
      )
    }

    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasItineraryData && <DownloadItineraryButton chatId={chatId} />}
        <NavBarRegionButton />
        {showCartButton && <CartButton
          itemCount={cartSummary.bookableItemCount}
          totalPrice={cartSummary.estimatedTotal}
          isLoading={cartSummary.isLoading}
          onClick={handleCartClick}
        />}
        <TextButton
          kind="secondary"
          size="medium"
          onClick={handleLogin}
        >
          {loginButtonText}
        </TextButton>
      </Group>
    )
  }, [isLoggedIn, hasItineraryData, chatId, handleLogin, loginButtonText, showCartButton, cartSummary.bookableItemCount, cartSummary.estimatedTotal, cartSummary.isLoading, handleCartClick])

  return (
    <Group direction="vertical" className={className}>
      <TertiaryHeader
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        fullWidth
      >
        <TextButton
          kind="tertiary"
          size="small"
          startIcon={<LineArrowLeftIcon />}
          to="/"
        >
          {backButtonText}
        </TextButton>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          <ContactOptions />
          <TextButton
            kind="tertiary"
            size="small"
            startIcon={<LineCommentEditIcon />}
            href="https://usabi.li/do/gizugyzacokd/vj9hxb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {giveFeedbackText}
          </TextButton>
        </Group>
      </TertiaryHeader>
      <StyledStickyHeader>
        <LeftContent>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <IconButton
              kind="tertiary"
              size="medium"
              onClick={handleOpenDrawer}
              aria-label={openMenuAriaLabel}
            >
              <LineBarsIcon />
            </IconButton>
            <Clickable to="/trip-planner/chat-trip-planner/new">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LogoWrapper>
                  <TripPlannerStyledLogo />
                </LogoWrapper>
                <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
                <Label kind="primary" variant="highlight">{betaLabel}</Label>
              </Group>
            </Clickable>
          </Group>
        </LeftContent>
        {rightContent && (
          <RightContent>
            {rightContent}
          </RightContent>
        )}
      </StyledStickyHeader>
    </Group>
  )
}

export default TripPlannerHeader
